import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: ReturnType<typeof io> | null;
  sshSocket: ReturnType<typeof io> | null;
  connected: boolean;
  sshConnected: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export function SocketProvider({ children }: SocketProviderProps) {
  const { user, token, loading } = useAuth();
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const [sshSocket, setSshSocket] = useState<ReturnType<typeof io> | null>(null);
  const [connected, setConnected] = useState(false);
  const [sshConnected, setSshConnected] = useState(false);

  useEffect(() => {
    if (loading || !user || !token) {
      setSocket(null);
      setSshSocket(null);
      setConnected(false);
      setSshConnected(false);
      return;
    }

    const newSocket = io({ auth: { token } });
    const newSshSocket = user.isAdmin ? io('/ssh', { auth: { token } }) : null;

    newSocket.on('connect', () => {
      console.log('Connected to server');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setConnected(false);
    });

    newSocket.on('connect_error', (error: Error) => {
      console.warn('Socket authentication failed:', error.message);
      setConnected(false);
    });

    newSshSocket?.on('connect', () => setSshConnected(true));
    newSshSocket?.on('disconnect', () => setSshConnected(false));
    newSshSocket?.on('connect_error', (error: Error) => {
      console.warn('SSH socket authentication failed:', error.message);
      setSshConnected(false);
    });

    setSocket(newSocket);
    setSshSocket(newSshSocket);

    return () => {
      newSocket.close();
      newSshSocket?.close();
      setConnected(false);
      setSshConnected(false);
    };
  }, [loading, token, user]);

  const value = useMemo(
    () => ({ socket, sshSocket, connected, sshConnected }),
    [socket, sshSocket, connected, sshConnected],
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
